import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { roomTypeService } from '../../services/api';
import { useUser } from '../../hooks/useUser';

const RoomTypesPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    isActive: true,
    sortOrder: 0
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomTypeToDelete, setRoomTypeToDelete] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Verificar se o usuário é admin
  const isAdmin = user?.role === 'ADMIN';

  // Carregar tipos de quarto
  const loadRoomTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await roomTypeService.getAll();
      setRoomTypes(data);
    } catch (err) {
      console.error('Erro ao carregar tipos de quarto:', err);
      setError('Erro ao carregar tipos de quarto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoomTypes();
  }, []);

  // Abrir formulário para criar novo
  const handleCreate = () => {
    setEditingRoomType(null);
    setFormData({
      name: '',
      label: '',
      description: '',
      isActive: true,
      sortOrder: roomTypes.length
    });
    setFormErrors({});
    setShowForm(true);
  };

  // Abrir formulário para editar
  const handleEdit = (roomType) => {
    setEditingRoomType(roomType);
    setFormData({
      name: roomType.name,
      label: roomType.label,
      description: roomType.description || '',
      isActive: roomType.isActive,
      sortOrder: roomType.sortOrder
    });
    setFormErrors({});
    setShowForm(true);
  };

  // Fechar formulário
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRoomType(null);
    setFormData({
      name: '',
      label: '',
      description: '',
      isActive: true,
      sortOrder: 0
    });
    setFormErrors({});
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    if (!formData.label.trim()) {
      errors.label = 'Label é obrigatório';
    }

    if (formData.sortOrder < 0) {
      errors.sortOrder = 'Ordem deve ser maior ou igual a zero';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar tipo de quarto
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      if (editingRoomType) {
        await roomTypeService.update(editingRoomType.id, formData);
      } else {
        await roomTypeService.create(formData);
      }

      await loadRoomTypes();
      handleCloseForm();
      
      // Mostrar notificação de sucesso
      if (typeof window !== 'undefined' && window.showNotificationModal) {
        window.showNotificationModal(
          'success', 
          'Sucesso!', 
          editingRoomType ? 'Tipo de quarto atualizado com sucesso!' : 'Tipo de quarto criado com sucesso!'
        );
      }
    } catch (err) {
      console.error('Erro ao salvar tipo de quarto:', err);
      setError('Erro ao salvar tipo de quarto');
      
      if (typeof window !== 'undefined' && window.showNotificationModal) {
        window.showNotificationModal('error', 'Erro', 'Erro ao salvar tipo de quarto. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Abrir modal de confirmação de exclusão
  const handleDelete = (roomType) => {
    setRoomTypeToDelete(roomType);
    setShowDeleteModal(true);
  };

  // Confirmar exclusão
  const confirmDelete = async () => {
    try {
      await roomTypeService.delete(roomTypeToDelete.id);
      await loadRoomTypes();
      setShowDeleteModal(false);
      setRoomTypeToDelete(null);
      
      if (typeof window !== 'undefined' && window.showNotificationModal) {
        window.showNotificationModal('success', 'Sucesso!', 'Tipo de quarto excluído com sucesso!');
      }
    } catch (err) {
      console.error('Erro ao excluir tipo de quarto:', err);
      setError('Erro ao excluir tipo de quarto');
      
      if (typeof window !== 'undefined' && window.showNotificationModal) {
        window.showNotificationModal('error', 'Erro', err.response?.data?.message || 'Erro ao excluir tipo de quarto.');
      }
    }
  };

  // Cancelar exclusão
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRoomTypeToDelete(null);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
        <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        
        <main className="lg:ml-60 pt-16">
          <div className="p-4 md:p-6">
            <Breadcrumb />
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Acesso Negado</h2>
              <p className="text-muted-foreground">
                Apenas administradores podem gerenciar tipos de quarto.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-4 md:p-6">
          {/* Breadcrumb */}
          <Breadcrumb />
          
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2"
              >
                <Icon name="ChevronLeft" size={16} />
                <span className="hidden md:inline">Voltar para Configurações</span>
              </Button>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Tipos de Quarto</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-2">
                  Gerencie os tipos de quarto disponíveis no sistema
                </p>
              </div>
              <Button onClick={handleCreate} className="flex items-center gap-2 w-full md:w-auto">
                <Icon name="Plus" size={20} />
                Novo Tipo
              </Button>
            </div>
          </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando tipos de quarto...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Nome</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Label</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Descrição</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Ordem</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Quartos</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {roomTypes.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground">
                        <Icon name="Bed" size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">Nenhum tipo de quarto encontrado</p>
                        <p className="text-sm">Clique em "Novo Tipo" para criar o primeiro tipo de quarto.</p>
                      </td>
                    </tr>
                  ) : (
                    roomTypes.map((roomType) => (
                      <tr key={roomType.id} className="hover:bg-muted/20">
                        <td className="px-6 py-4 text-sm font-medium text-foreground">
                          {roomType.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {roomType.label}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {roomType.description || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            roomType.isActive 
                              ? 'bg-success/20 text-success' 
                              : 'bg-muted/20 text-muted-foreground'
                          }`}>
                            {roomType.isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {roomType.sortOrder}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {roomType._count?.rooms || 0}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(roomType)}
                              className="text-primary hover:text-primary/80"
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(roomType)}
                              className="text-destructive hover:text-destructive/80"
                              disabled={roomType._count?.rooms > 0}
                              title={roomType._count?.rooms > 0 ? 'Não é possível excluir - há quartos usando este tipo' : 'Excluir'}
                            >
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-elevation-3">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground">
                  {editingRoomType ? 'Editar Tipo de Quarto' : 'Novo Tipo de Quarto'}
                </h2>
                <Button variant="ghost" size="icon" onClick={handleCloseForm}>
                  <Icon name="X" size={20} />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <Input
                  label="Nome *"
                  placeholder="Ex: STANDARD"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  error={formErrors.name}
                />

                <Input
                  label="Label *"
                  placeholder="Ex: Standard"
                  value={formData.label}
                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                  error={formErrors.label}
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Descrição
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Descrição do tipo de quarto..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="rounded border-border"
                      />
                      <span className="text-sm font-medium text-foreground">Ativo</span>
                    </label>
                  </div>

                  <Input
                    label="Ordem"
                    type="number"
                    min="0"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
                    error={formErrors.sortOrder}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
                <Button variant="outline" onClick={handleCloseForm} disabled={submitting}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={submitting}>
                  {submitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Salvando...</span>
                    </div>
                  ) : (
                    editingRoomType ? 'Atualizar' : 'Criar'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && roomTypeToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-elevation-3">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-destructive/20 rounded-lg">
                    <Icon name="AlertTriangle" size={24} className="text-destructive" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Confirmar Exclusão</h3>
                    <p className="text-sm text-muted-foreground">
                      Esta ação não pode ser desfeita
                    </p>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 mb-6">
                  <p className="text-sm text-foreground mb-2">
                    Você está prestes a excluir o tipo de quarto:
                  </p>
                  <div className="font-medium text-foreground">
                    {roomTypeToDelete.name} - {roomTypeToDelete.label}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                  <Button variant="outline" onClick={cancelDelete}>
                    Cancelar
                  </Button>
                  <Button variant="destructive" onClick={confirmDelete}>
                    Excluir
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default RoomTypesPage;

